import * as Sentry from "@sentry/react";

// Configuration from environment variables
const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED === 'true';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const SENTRY_TRACES_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.02');
const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.01');
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');
const SENTRY_DEBUG = import.meta.env.VITE_SENTRY_DEBUG === 'true';

/**
 * Initialize Sentry error tracking and performance monitoring
 * Called once at application startup
 */
export function initSentry() {
  if (!SENTRY_ENABLED) {
    console.log('[Sentry] Disabled via VITE_SENTRY_ENABLED environment variable');
    return;
  }

  if (!SENTRY_DSN) {
    console.warn('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    debug: SENTRY_DEBUG,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track all React Router routes
        tracePropagationTargets: [
          "localhost",
          "jetogo.local",
          /^https:\/\/jetogo-frontend\.onrender\.com/,
          /^https:\/\/jetogo-backend\.onrender\.com/,
        ],
      }),

      // Session replay for debugging
      Sentry.replayIntegration({
        maskAllText: true,        // Hide all text content
        blockAllMedia: true,      // Hide images/video
        maskAllInputs: true,      // Hide form inputs
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,

    // Session Replay - only sample a small percentage in production
    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    // But always replay when an error occurs
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,

    // Release tracking (populated by build script)
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Security: Filter sensitive data before sending to Sentry
    beforeSend(event, _hint) {
      // Remove sensitive form data
      if (event.request?.data) {
        // Safely convert to a plain object — axios error objects and other
        // circular structures will throw "Converting circular structure to JSON"
        // if Sentry tries to serialize them. Flatten to a safe plain object first.
        //
        // NOTE: never return early from here — the header/URL scrubbing below
        // must still run, otherwise an unserializable payload (the very case
        // this handles) would ship Authorization and cookie headers to Sentry.
        let safeData;
        try {
          const stringified = JSON.stringify(event.request.data);
          // JSON.stringify returns undefined (not a string) for values like
          // Functions, Symbols, or top-level undefined — guard before parsing
          // to avoid "JSON Parse error: Unexpected identifier 'undefined'" on Safari
          safeData = stringified === undefined ? null : JSON.parse(stringified);
        } catch {
          // Circular or non-serializable — drop the request data entirely
          safeData = null;
        }

        if (safeData === null || typeof safeData !== 'object') {
          event.request.data = '[unserializable]';
        } else {
          const data = safeData;

          // Redact sensitive fields
          const sensitiveFields = [
            'password', 'newPassword', 'currentPassword', 'confirmPassword',
            'token', 'accessToken', 'refreshToken', 'apiKey',
            'creditCard', 'cardNumber', 'cvv', 'cvc',
            'ssn', 'socialSecurityNumber',
          ];

          sensitiveFields.forEach(field => {
            if (data[field]) {
              data[field] = '[REDACTED]';
            }
          });

          event.request.data = data;
        }
      }

      // Remove authorization headers
      if (event.request?.headers) {
        const headers = { ...event.request.headers };
        delete headers.authorization;
        delete headers.cookie;
        delete headers['x-api-key'];
        event.request.headers = headers;
      }

      // Filter URLs to remove sensitive query parameters
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.searchParams.delete('token');
          url.searchParams.delete('key');
          url.searchParams.delete('password');
          event.request.url = url.toString();
        } catch {
          // Invalid URL, keep as is
        }
      }

      return event;
    },

    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      'top.GECKO',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',

      // Random plugins/extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',

      // Facebook errors
      'fb_xd_fragment',

      // ISP injected errors
      '__gCrWeb',
      '__gCrWebChannelController',

      // Browser quirks
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',

      // Network errors (expected)
      'NetworkError',
      'Network request failed',

      // Non-Error promise rejections
      'Non-Error promise rejection captured',
    ],

    // Don't send errors from these URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });

  console.log(`[Sentry] Initialized for ${SENTRY_ENVIRONMENT} environment`);
}

/**
 * Set the current user context for error tracking
 * @param {Object} user - User object with id, email, name
 */
export function setSentryUser(user) {
  if (!SENTRY_ENABLED) return;

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    // Include plan type for better error context
    plan_type: user.plan_type || 'unknown',
    role: user.role || 'customer',
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  if (!SENTRY_ENABLED) return;
  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export function captureSentryError(error, context = {}) {
  if (!SENTRY_ENABLED) {
    console.error('Error:', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message (non-error event)
 * @param {string} message - The message to capture
 * @param {string} level - Severity level (info, warning, error)
 * @param {Object} context - Additional context
 */
export function captureSentryMessage(message, level = 'info', context = {}) {
  if (!SENTRY_ENABLED) {
    console.log(`[${level}]`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Add breadcrumb for debugging
 * @param {string} message - Breadcrumb message
 * @param {string} category - Breadcrumb category
 * @param {Object} data - Additional data
 */
export function addSentryBreadcrumb(message, category = 'custom', data = {}) {
  if (!SENTRY_ENABLED) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Check if Sentry is enabled
 * @returns {boolean}
 */
export function isSentryEnabled() {
  return SENTRY_ENABLED;
}
